'use client'

import { createSeededOrder } from './bracket-maker/seeding'

import * as React from 'react'
import NextImage from 'next/image'
import { flushSync } from 'react-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { Button } from '@/components/ui/button'
import { usePlausible } from 'next-plausible'
import { useLocale, useTranslations } from 'next-intl'
import {
  ArrowLeftRight,
  ArrowUpDown,
  Columns,
  ImageIcon,
  Network,
  Type,
} from 'lucide-react'

import { ImageSelectorDialog } from '@/components/image-selector-dialog'

import {
  APPEARANCE_STORAGE_KEY,
  DEFAULT_APPEARANCE,
  normalizeAppearance,
  type BracketAppearance,
} from './bracket-maker/appearance'

import { BracketCanvas } from './bracket-maker/canvas'
import {
  BRACKET_EXAMPLE_EVENT,
  createBracketExample,
  hasBracketEdits,
} from './bracket-maker/examples'
import { BracketViewport } from './bracket-maker/bracket-viewport'
import type { BracketVisibleArea } from './bracket-maker/viewport'
import {
  BRACKET_BACKGROUND_FIT_OPTIONS,
  BRACKET_BACKGROUND_POSITION_OPTIONS,
  BRACKET_BACKGROUND_PRESETS,
  BRACKET_BACKGROUND_STORAGE_KEY,
  DEFAULT_BRACKET_BACKGROUND_SETTINGS,
  DEFAULT_EMPTY_TEAMS,
  MAX_TEAMS,
  MIN_TEAMS,
  clampZoomValue,
  createBracketTheme,
  getDefaultTeamImage,
  isDefaultTeamImage,
} from './bracket-maker/constants'
import { BracketControlsPanel } from './bracket-maker/controls-panel'
import {
  buildBracketConnectors,
  buildColumnHeaders,
  buildVerticalBracketConnectors,
  computeBracketDimensions,
  computeBracketLayout,
  computeRenderedBracketDimensions,
  getChampionPlacement,
} from './bracket-maker/layout'
import { getBracketExportOptions } from './bracket-maker/export-options'
import {
  createBracketExportImage,
  triggerBracketDownload,
} from './bracket-maker/export-image'
import {
  shuffleSeedOrder,
  reorderSeeds,
  buildRounds,
  buildSeeds,
  clampTeams,
  clearDownstreamSelections,
  updateSelectionsAfterSeedRename,
  createPlaceholderImages,
  createSample,
  nextPowerOfTwo,
} from './bracket-maker/model'
import { BracketToolbar } from './bracket-maker/toolbar'
import type {
  BracketBackgroundFit,
  BracketBackgroundPosition,
  BracketBackgroundPresetId,
  BracketBackgroundSettings,
  BracketDirection,
  BracketDisplayMode,
  BracketMakerCopy,
  BracketMakerProps,
  BracketStructure,
  ColumnHeader,
  DisplayModeOption,
  DirectionOption,
  SelectionMap,
  StructureOption,
  WinnerSide,
} from './bracket-maker/types'

const EXPORT_WATERMARK_TEXT = 'snapbracket.com'
const EXPORT_WATERMARK_FONT_SIZE = 13
const EXPORT_WATERMARK_BOTTOM_OFFSET = 12
const EXPORT_WATERMARK_COLOR = 'rgba(15, 23, 42, 0.2)'
const BACKGROUND_UPLOAD_MAX_WIDTH = 1800
const BACKGROUND_UPLOAD_MAX_HEIGHT = 1200
const BACKGROUND_UPLOAD_QUALITY = 0.84

const BACKGROUND_PRESET_IDS = new Set<BracketBackgroundPresetId>([
  'none',
  ...BRACKET_BACKGROUND_PRESETS.map((preset) => preset.id),
  'custom',
])

function drawExportWatermark(canvas: HTMLCanvasElement, exportWidth: number) {
  const context = canvas.getContext('2d')
  if (!context) return

  const pixelRatio = exportWidth > 0 ? canvas.width / exportWidth : 1

  context.save()
  context.font = `500 ${EXPORT_WATERMARK_FONT_SIZE * pixelRatio}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`
  context.fillStyle = EXPORT_WATERMARK_COLOR
  context.textAlign = 'center'
  context.textBaseline = 'bottom'
  context.fillText(
    EXPORT_WATERMARK_TEXT,
    canvas.width / 2,
    canvas.height - EXPORT_WATERMARK_BOTTOM_OFFSET * pixelRatio,
  )
  context.restore()
}

function clampNumber(
  value: unknown,
  fallback: number,
  min: number,
  max: number,
) {
  const numericValue =
    typeof value === 'number' ? value : Number.parseFloat(String(value))

  if (!Number.isFinite(numericValue)) return fallback

  return Number(Math.min(max, Math.max(min, numericValue)).toFixed(2))
}

function isBackgroundPresetId(
  value: unknown,
): value is BracketBackgroundPresetId {
  return (
    typeof value === 'string' &&
    BACKGROUND_PRESET_IDS.has(value as BracketBackgroundPresetId)
  )
}

function isBackgroundFit(value: unknown): value is BracketBackgroundFit {
  return (
    typeof value === 'string' &&
    BRACKET_BACKGROUND_FIT_OPTIONS.includes(value as BracketBackgroundFit)
  )
}

function isBackgroundPosition(
  value: unknown,
): value is BracketBackgroundPosition {
  return (
    typeof value === 'string' &&
    BRACKET_BACKGROUND_POSITION_OPTIONS.includes(
      value as BracketBackgroundPosition,
    )
  )
}

function normalizeBackgroundSettings(
  value: unknown,
): BracketBackgroundSettings {
  if (!value || typeof value !== 'object') {
    return { ...DEFAULT_BRACKET_BACKGROUND_SETTINGS }
  }

  const raw = value as Partial<BracketBackgroundSettings>

  return {
    ...DEFAULT_BRACKET_BACKGROUND_SETTINGS,
    preset: isBackgroundPresetId(raw.preset)
      ? raw.preset
      : DEFAULT_BRACKET_BACKGROUND_SETTINGS.preset,
    customImage:
      typeof raw.customImage === 'string' &&
      raw.customImage.startsWith('data:image/')
        ? raw.customImage
        : null,
    fit: isBackgroundFit(raw.fit)
      ? raw.fit
      : DEFAULT_BRACKET_BACKGROUND_SETTINGS.fit,
    position: isBackgroundPosition(raw.position)
      ? raw.position
      : DEFAULT_BRACKET_BACKGROUND_SETTINGS.position,
    opacity: clampNumber(
      raw.opacity,
      DEFAULT_BRACKET_BACKGROUND_SETTINGS.opacity,
      0,
      1,
    ),
    overlayOpacity: clampNumber(
      raw.overlayOpacity,
      DEFAULT_BRACKET_BACKGROUND_SETTINGS.overlayOpacity,
      0,
      0.85,
    ),
    overlayTone:
      raw.overlayTone === 'dark' || raw.overlayTone === 'light'
        ? raw.overlayTone
        : DEFAULT_BRACKET_BACKGROUND_SETTINGS.overlayTone,
    blur: clampNumber(raw.blur, DEFAULT_BRACKET_BACKGROUND_SETTINGS.blur, 0, 8),
    print:
      typeof raw.print === 'boolean'
        ? raw.print
        : DEFAULT_BRACKET_BACKGROUND_SETTINGS.print,
  }
}

function getInitialBackgroundSettings(): BracketBackgroundSettings {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_BRACKET_BACKGROUND_SETTINGS }
  }

  try {
    const raw = window.localStorage.getItem(BRACKET_BACKGROUND_STORAGE_KEY)
    if (!raw) return { ...DEFAULT_BRACKET_BACKGROUND_SETTINGS }
    return normalizeBackgroundSettings(JSON.parse(raw))
  } catch {
    return { ...DEFAULT_BRACKET_BACKGROUND_SETTINGS }
  }
}

function persistBackgroundSettings(settings: BracketBackgroundSettings) {
  if (typeof window === 'undefined') return

  try {
    window.localStorage.setItem(
      BRACKET_BACKGROUND_STORAGE_KEY,
      JSON.stringify(settings),
    )
  } catch {
    try {
      window.localStorage.setItem(
        BRACKET_BACKGROUND_STORAGE_KEY,
        JSON.stringify({
          ...settings,
          preset: settings.preset === 'custom' ? 'none' : settings.preset,
          customImage: null,
        }),
      )
    } catch {
      // Ignore local storage quota or privacy-mode errors.
    }
  }
}

function loadBackgroundImage(file: File) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = (error) => {
      URL.revokeObjectURL(url)
      reject(error)
    }
    image.src = url
  })
}

async function compressBackgroundImage(file: File) {
  const image = await loadBackgroundImage(file)
  const sourceWidth = image.naturalWidth || image.width
  const sourceHeight = image.naturalHeight || image.height
  const scale = Math.min(
    1,
    BACKGROUND_UPLOAD_MAX_WIDTH / sourceWidth,
    BACKGROUND_UPLOAD_MAX_HEIGHT / sourceHeight,
  )
  const width = Math.max(1, Math.round(sourceWidth * scale))
  const height = Math.max(1, Math.round(sourceHeight * scale))
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height

  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas unavailable')

  context.drawImage(image, 0, 0, width, height)

  return canvas.toDataURL('image/webp', BACKGROUND_UPLOAD_QUALITY)
}

export function BracketMaker({
  initialTeamCount,
  seededPlacement = false,
  theme: themeOverride,
  examplePreset,
}: BracketMakerProps) {
  const t = useTranslations()
  const locale = useLocale()
  const plausible = usePlausible()
  const theme = React.useMemo(() => {
    const base = createBracketTheme(themeOverride)
    for (const key of [
      'slotName',
      'slotNameButton',
      'slotTextName',
      'slotTextNameButton',
      'slotTextInput',
      'matchVs',
      'roundHeader',
      'roundHeaderBadge',
      'championBadge',
    ] as const) {
      base[key] += ' bracket-text'
    }
    for (const key of [
      'slotBase',
      'championCard',
      'roundHeader',
      'roundHeaderBadge',
      'championBadge',
    ] as const) {
      base[key] += ' bracket-card'
    }
    base.connector += ' bracket-lines'
    return base
  }, [themeOverride])
  const initialCount = clampTeams(initialTeamCount ?? 8)
  const [isUpdating, startTransition] = React.useTransition()
  const previousInitialCount = React.useRef(initialCount)
  const [teamCount, setTeamCount] = React.useState<number>(initialCount)

  const defaultBracketTitle = React.useMemo(
    () => t('maker.bracketTitleDefault'),
    [t],
  )
  const copy = React.useMemo<BracketMakerCopy>(
    () => ({
      autoWin: t('maker.autoWin'),
      background: {
        title: t('maker.background.title'),
        upload: t('maker.background.upload'),
        remove: t('maker.background.remove'),
        opacity: t('maker.background.opacity'),
        overlay: t('maker.background.overlay'),
        blur: t('maker.background.blur'),
        fitLabel: t('maker.background.fitLabel'),
        positionLabel: t('maker.background.positionLabel'),
        toneLight: t('maker.background.toneLight'),
        toneDark: t('maker.background.toneDark'),
        print: t('maker.background.print'),
        presets: {
          none: t('maker.background.presets.none'),
          stadium: t('maker.background.presets.stadium'),
          basketball: t('maker.background.presets.basketball'),
          paper: t('maker.background.presets.paper'),
          esports: t('maker.background.presets.esports'),
          custom: t('maker.background.presets.custom'),
        },
        fits: {
          cover: t('maker.background.fits.cover'),
          contain: t('maker.background.fits.contain'),
          tile: t('maker.background.fits.tile'),
        },
        positions: {
          center: t('maker.background.positions.center'),
          top: t('maker.background.positions.top'),
          bottom: t('maker.background.positions.bottom'),
        },
      },
      bulkUpload: t('maker.bulkUpload'),
      bye: t('maker.bye'),
      champion: t('maker.champion'),
      clearList: t('maker.clearList'),
      displayModeLabel: t('maker.displayModeLabel'),
      directionLabel: t('maker.directionLabel'),
      editTitle: t('maker.editTitle'),
      exportPng: t('maker.exportPng'),
      exporting: t('maker.exporting'),
      updating: t('maker.updating'),
      editor: {
        appearance: t('maker.editor.appearance'),
        closePanel: t('maker.editor.closePanel'),
        editTeams: t('maker.editor.editTeams'),
        setup: t('maker.editor.setup'),
        teamCountSummary: t('maker.editor.teamCountSummary', {
          count: teamCount,
        }),
      },
      exitFullscreen: t('maker.exitFullscreen'),
      fillSample: t('maker.fillSample'),
      fullscreen: t('maker.fullscreen'),
      hideNames: t('maker.hideNames'),
      imageMode: t('maker.displayMode.image'),
      imageModeHint: t('maker.displayMode.imageHint'),
      matchPlural: 'Matches',
      matchSingular: 'Match',
      namesPlaceholder: t('maker.namesPlaceholder'),
      participants: t('maker.participants'),
      resetWinners: t('maker.resetWinners'),
      saveTitle: t('maker.saveTitle'),
      showNames: t('maker.showNames'),
      structureLabel: t('maker.structureLabel'),
      teams: t('maker.teams'),
      textMode: t('maker.displayMode.text'),
      textModeHint: t('maker.displayMode.textHint'),
      zoomIn: t('maker.zoomIn'),
      zoomOut: t('maker.zoomOut'),
    }),
    [t, teamCount],
  )

  const [participants, setParticipants] = React.useState<string[]>(() =>
    createSample(initialCount),
  )
  const [participantImages, setParticipantImages] = React.useState<
    (string | null)[]
  >(() => createPlaceholderImages(initialCount))
  const [isImageDialogOpen, setIsImageDialogOpen] = React.useState(false)
  const [targetImageIndex, setTargetImageIndex] = React.useState<number | null>(
    null,
  )
  const [backgroundSettings, setBackgroundSettings] =
    React.useState<BracketBackgroundSettings>(() => ({
      ...DEFAULT_BRACKET_BACKGROUND_SETTINGS,
    }))
  const [backgroundSettingsLoaded, setBackgroundSettingsLoaded] =
    React.useState(false)

  const [structure, setStructure] = React.useState<BracketStructure>(
    seededPlacement ? 'single' : 'two-sided',
  )
  const [direction, setDirection] =
    React.useState<BracketDirection>('horizontal')
  const [displayMode, setDisplayMode] = React.useState<BracketDisplayMode>(
    seededPlacement ? 'text' : 'image',
  )
  const [appearance, setAppearance] =
    React.useState<BracketAppearance>(DEFAULT_APPEARANCE)
  const [appearanceLoaded, setAppearanceLoaded] = React.useState(false)
  const [appearanceStorageError, setAppearanceStorageError] =
    React.useState(false)
  const [seedOrder, setSeedOrder] = React.useState<number[] | null>(() =>
    seededPlacement ? createSeededOrder(initialCount) : null,
  )
  const [shuffleCount, setShuffleCount] = React.useState(0)
  React.useEffect(() => {
    try {
      const saved = window.localStorage.getItem(APPEARANCE_STORAGE_KEY)
      if (saved) setAppearance(normalizeAppearance(JSON.parse(saved)))
    } catch {
      // 存储不可用时仍可在当前页面调整外观。
    }
    setAppearanceLoaded(true)
  }, [])
  React.useEffect(() => {
    if (!appearanceLoaded) return
    try {
      window.localStorage.setItem(
        APPEARANCE_STORAGE_KEY,
        JSON.stringify(appearance),
      )
      setAppearanceStorageError(false)
    } catch {
      setAppearanceStorageError(true)
    }
  }, [appearance, appearanceLoaded])

  const [selections, setSelections] = React.useState<SelectionMap>({})

  React.useEffect(() => {
    if (
      initialTeamCount &&
      clampTeams(initialTeamCount) !== previousInitialCount.current
    ) {
      previousInitialCount.current = clampTeams(initialTeamCount)
      const next = clampTeams(initialTeamCount)
      setSeedOrder(seededPlacement ? createSeededOrder(next) : null)
      setTeamCount(next)
      setParticipants(createSample(next))
      setParticipantImages(createPlaceholderImages(next))
      setSelections({})
    }
  }, [initialTeamCount, seededPlacement])
  const [zoom, setZoom] = React.useState(1)
  const [isFullscreen, setIsFullscreen] = React.useState(false)
  const [canFullscreen, setCanFullscreen] = React.useState(false)
  const [isExporting, setIsExporting] = React.useState(false)
  const exportMountedRef = React.useRef(true)
  const [exportImageUrl, setExportImageUrl] = React.useState<string | null>(
    null,
  )
  const [exportError, setExportError] = React.useState<string | null>(null)
  const [showExportPreview, setShowExportPreview] = React.useState(false)
  const [isPrinting, setIsPrinting] = React.useState(false)
  const [showNames, setShowNames] = React.useState(true)
  const [portalTarget, setPortalTarget] = React.useState<HTMLElement | null>(
    null,
  )
  const [bracketTitle, setBracketTitle] = React.useState(defaultBracketTitle)
  const [isEditingTitle, setIsEditingTitle] = React.useState(false)

  const [isExampleConfirmationOpen, setIsExampleConfirmationOpen] =
    React.useState(false)
  const [exampleLoadVersion, setExampleLoadVersion] = React.useState(0)
  const applyBracketExample = React.useCallback(() => {
    if (!examplePreset || isExporting || isUpdating) return
    const example = createBracketExample(examplePreset)
    const exampleStructure =
      examplePreset === 'pictures' ? 'two-sided' : 'single'
    setTeamCount(8)
    setParticipants(example.participants)
    setParticipantImages(example.images)
    setSelections(example.selections)
    setSeedOrder(null)
    setStructure(exampleStructure)
    setDirection('horizontal')
    setDisplayMode(examplePreset === 'pictures' ? 'image' : 'text')
    setShowNames(true)
    setBracketTitle(t('maker.exampleTitle'))
    setIsEditingTitle(false)
    // 重建视口时同时重置拖拽，并应用统一的首次缩放规则。
    setExampleLoadVersion((version) => version + 1)
    setExportImageUrl(null)
    setExportError(null)
    setShowExportPreview(false)
    try {
      plausible('BracketExampleLoad', {
        props: { example: examplePreset, locale, team_count: 8 },
      })
    } catch {}
    containerRef.current
      ?.closest('section')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [examplePreset, isExporting, isUpdating, t, plausible, locale])

  React.useEffect(() => {
    if (!examplePreset) return
    const loadExample = (event: Event) => {
      if (isExporting || isUpdating) return
      if (!(event instanceof CustomEvent) || event.detail !== examplePreset)
        return
      const edited = hasBracketEdits({
        participants,
        images: participantImages,
        selections,
        title: bracketTitle,
        defaultTitle: defaultBracketTitle,
        teamCount,
        seedOrder,
      })
      if (edited) setIsExampleConfirmationOpen(true)
      else applyBracketExample()
    }
    window.addEventListener(BRACKET_EXAMPLE_EVENT, loadExample)
    return () => window.removeEventListener(BRACKET_EXAMPLE_EVENT, loadExample)
  }, [
    examplePreset,
    participants,
    participantImages,
    selections,
    bracketTitle,
    defaultBracketTitle,
    teamCount,
    seedOrder,
    isExporting,
    isUpdating,
    applyBracketExample,
  ])

  React.useEffect(() => {
    exportMountedRef.current = true
    return () => {
      exportMountedRef.current = false
    }
  }, [])

  React.useEffect(() => {
    return () => {
      if (exportImageUrl) URL.revokeObjectURL(exportImageUrl)
    }
  }, [exportImageUrl])

  const resetSelections = React.useCallback(() => setSelections({}), [])

  const updateBackgroundSettings = React.useCallback(
    (value: Partial<BracketBackgroundSettings>) => {
      setBackgroundSettings((current) => ({ ...current, ...value }))
    },
    [],
  )

  const handleBackgroundPresetChange = React.useCallback(
    (presetId: BracketBackgroundPresetId) => {
      setBackgroundSettings((current) => {
        if (presetId === 'none' || presetId === 'custom') {
          return { ...current, preset: presetId }
        }

        const preset = BRACKET_BACKGROUND_PRESETS.find(
          (item) => item.id === presetId,
        )

        return {
          ...current,
          preset: presetId,
          opacity: preset?.defaultOpacity ?? current.opacity,
          overlayOpacity:
            preset?.defaultOverlayOpacity ?? current.overlayOpacity,
          overlayTone: preset?.defaultOverlayTone ?? current.overlayTone,
        }
      })
    },
    [],
  )

  const handleBackgroundRemove = React.useCallback(() => {
    setBackgroundSettings((current) => ({
      ...current,
      preset: current.preset === 'custom' ? 'none' : current.preset,
      customImage: null,
    }))
  }, [])

  const handleBackgroundUpload = React.useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return

    try {
      const customImage = await compressBackgroundImage(file)
      setBackgroundSettings((current) => ({
        ...current,
        preset: 'custom',
        customImage,
        fit: 'cover',
        position: 'center',
        opacity: 0.72,
        overlayOpacity: 0.42,
        overlayTone: 'light',
      }))
    } catch (error) {
      console.error(error)
    }
  }, [])

  const containerRef = React.useRef<HTMLDivElement>(null)
  const bracketRef = React.useRef<HTMLDivElement>(null)
  const titleInputRef = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    setBracketTitle((value) => (value.trim() ? value : defaultBracketTitle))
  }, [defaultBracketTitle])

  React.useEffect(() => {
    setBackgroundSettings(getInitialBackgroundSettings())
    setBackgroundSettingsLoaded(true)
  }, [])

  React.useEffect(() => {
    if (!backgroundSettingsLoaded) return
    persistBackgroundSettings(backgroundSettings)
  }, [backgroundSettings, backgroundSettingsLoaded])

  React.useEffect(() => {
    if (isEditingTitle) {
      titleInputRef.current?.focus()
      titleInputRef.current?.select()
    }
  }, [isEditingTitle])

  React.useEffect(() => {
    setCanFullscreen(
      typeof document !== 'undefined' &&
        (!!document.fullscreenEnabled ||
          typeof document.documentElement?.requestFullscreen === 'function'),
    )
    setPortalTarget(typeof document !== 'undefined' ? document.body : null)

    const handleFullScreenChange = () => {
      setIsFullscreen(document.fullscreenElement === containerRef.current)
    }

    document.addEventListener('fullscreenchange', handleFullScreenChange)
    const handleBeforePrint = () => flushSync(() => setIsPrinting(true))
    const handleAfterPrint = () => setIsPrinting(false)
    window.addEventListener('beforeprint', handleBeforePrint)
    window.addEventListener('afterprint', handleAfterPrint)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange)
      window.removeEventListener('beforeprint', handleBeforePrint)
      window.removeEventListener('afterprint', handleAfterPrint)
    }
  }, [])

  const normalizedTeamCount = React.useMemo(
    () => clampTeams(teamCount),
    [teamCount],
  )

  const totalSlots = React.useMemo(
    () => nextPowerOfTwo(normalizedTeamCount),
    [normalizedTeamCount],
  )

  const seeds = React.useMemo(
    () =>
      reorderSeeds(
        buildSeeds(
          participants,
          participantImages,
          normalizedTeamCount,
          totalSlots,
        ),
        seedOrder,
      ),
    [
      normalizedTeamCount,
      participantImages,
      participants,
      totalSlots,
      seedOrder,
    ],
  )

  const rounds = React.useMemo(
    () => buildRounds(seeds, selections),
    [seeds, selections],
  )

  const champion = rounds.at(-1)?.[0]?.winner ?? ''

  const roundShape = React.useMemo(
    () => buildRounds(buildSeeds([], [], normalizedTeamCount, totalSlots), {}),
    [normalizedTeamCount, totalSlots],
  )
  const layout = React.useMemo(
    () => computeBracketLayout(roundShape, structure, totalSlots),
    [roundShape, structure, totalSlots],
  )
  const matchLayout = layout.matchLayout

  const dimensions = React.useMemo(
    () => computeBracketDimensions(layout, structure, totalSlots),
    [layout, structure, totalSlots],
  )
  const { championColumnIndex, columnOffsets, columnWidths } = dimensions
  const renderedDimensions = React.useMemo(
    () => computeRenderedBracketDimensions(dimensions, direction),
    [dimensions, direction],
  )
  const { bracketHeight, bracketWidth, verticalXOffset } = renderedDimensions
  const shouldReverseVerticalAxis =
    direction === 'vertical' && structure === 'single'
  const activeBackgroundImage = React.useMemo(() => {
    if (backgroundSettings.preset === 'custom') {
      return backgroundSettings.customImage
    }

    return (
      BRACKET_BACKGROUND_PRESETS.find(
        (preset) => preset.id === backgroundSettings.preset,
      )?.image ?? null
    )
  }, [backgroundSettings.customImage, backgroundSettings.preset])
  const backgroundLayerStyle = React.useMemo<React.CSSProperties | null>(() => {
    if (!activeBackgroundImage) return null

    const tiled = backgroundSettings.fit === 'tile'

    return {
      backgroundImage: `url(${JSON.stringify(activeBackgroundImage)})`,
      backgroundPosition: backgroundSettings.position,
      backgroundRepeat: tiled ? 'repeat' : 'no-repeat',
      backgroundSize: tiled ? '360px 360px' : backgroundSettings.fit,
      filter:
        backgroundSettings.blur > 0
          ? `blur(${backgroundSettings.blur}px)`
          : undefined,
      opacity: backgroundSettings.opacity,
      transform: backgroundSettings.blur > 0 ? 'scale(1.03)' : undefined,
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    }
  }, [
    activeBackgroundImage,
    backgroundSettings.blur,
    backgroundSettings.fit,
    backgroundSettings.opacity,
    backgroundSettings.position,
  ])
  const backgroundOverlayStyle = React.useMemo<React.CSSProperties>(
    () => ({
      backgroundColor:
        backgroundSettings.overlayTone === 'dark'
          ? `rgba(15, 23, 42, ${backgroundSettings.overlayOpacity})`
          : `rgba(255, 255, 255, ${backgroundSettings.overlayOpacity})`,
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    }),
    [backgroundSettings.overlayOpacity, backgroundSettings.overlayTone],
  )

  const championPlacement = React.useMemo(() => {
    return getChampionPlacement(matchLayout, championColumnIndex)
  }, [championColumnIndex, matchLayout])

  const connectors = React.useMemo(() => {
    if (direction === 'vertical') {
      return buildVerticalBracketConnectors(
        matchLayout,
        columnOffsets,
        columnWidths,
        championPlacement,
        verticalXOffset,
        dimensions.bracketWidth,
        shouldReverseVerticalAxis,
        displayMode,
      )
    }

    const baseConnectors = buildBracketConnectors(
      matchLayout,
      columnOffsets,
      columnWidths,
      structure,
      championColumnIndex,
      displayMode,
    )

    return baseConnectors
  }, [
    championColumnIndex,
    championPlacement,
    columnOffsets,
    columnWidths,
    direction,
    dimensions.bracketWidth,
    displayMode,
    matchLayout,
    shouldReverseVerticalAxis,
    structure,
    verticalXOffset,
  ])

  const columnHeaders = React.useMemo<ColumnHeader[]>(() => {
    return buildColumnHeaders(
      matchLayout,
      layout.columnCount,
      rounds.length,
      structure,
      championColumnIndex,
      {
        champion: copy.champion,
        final: t('maker.finalLabel'),
        round: (round) => t('maker.roundLabel', { round }),
      },
    )
  }, [
    championColumnIndex,
    copy.champion,
    layout.columnCount,
    matchLayout,
    rounds.length,
    structure,
    t,
  ])

  const structureOptions = React.useMemo<StructureOption[]>(
    () => [
      {
        value: 'two-sided' as BracketStructure,
        label: t('maker.structure.twoSided'),
        hint: t('maker.structure.twoSidedHint'),
        icon: Columns,
      },
      {
        value: 'single' as BracketStructure,
        label: t('maker.structure.single'),
        hint: t('maker.structure.singleHint'),
        icon: Network,
      },
      // {
      //   value: 'regions' as BracketStructure,
      //   label: t('maker.structure.regions'),
      //   hint: t('maker.structure.regionsHint'),
      // },
    ],
    [t],
  )
  const directionOptions = React.useMemo<DirectionOption[]>(
    () => [
      {
        value: 'horizontal',
        label: t('maker.direction.horizontal'),
        hint: t('maker.direction.horizontalHint'),
        icon: ArrowLeftRight,
      },
      {
        value: 'vertical',
        label: t('maker.direction.vertical'),
        hint: t('maker.direction.verticalHint'),
        icon: ArrowUpDown,
      },
    ],
    [t],
  )
  const displayModeOptions = React.useMemo<DisplayModeOption[]>(
    () => [
      {
        value: 'image',
        label: copy.imageMode,
        hint: copy.imageModeHint,
        icon: ImageIcon,
      },
      {
        value: 'text',
        label: copy.textMode,
        hint: copy.textModeHint,
        icon: Type,
      },
    ],
    [copy.imageMode, copy.imageModeHint, copy.textMode, copy.textModeHint],
  )

  const handleTeamCountChange = (value: number) => {
    const next = clampTeams(value)
    // 大对阵图在可中断的更新中渲染，先让浏览器显示操作反馈。
    startTransition(() => {
      setSeedOrder(seededPlacement ? createSeededOrder(next) : null)
      setTeamCount(next)
      setParticipants(createSample(next))
      setParticipantImages(createPlaceholderImages(next))
      setSelections({})
    })
  }

  const handleNamesChange = (value: string) => {
    const names = value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .slice(0, MAX_TEAMS)

    const hasAnyName = names.some((name) => name.length > 0)
    const baseCount = hasAnyName ? names.length : DEFAULT_EMPTY_TEAMS
    const nextCount = clampTeams(Math.max(baseCount, MIN_TEAMS))
    const nextNames = names.slice(0, nextCount)
    while (nextNames.length < nextCount) nextNames.push('')

    startTransition(() => {
      if (nextCount !== teamCount)
        setSeedOrder(seededPlacement ? createSeededOrder(nextCount) : null)
      setTeamCount(nextCount)
      setParticipants(nextNames)
      setParticipantImages((prev) => {
        const nextImages = prev.slice(0, nextCount)
        while (nextImages.length < nextCount)
          nextImages.push(getDefaultTeamImage(nextImages.length))
        return nextImages.map((image, index) => {
          const hasName = nextNames[index]?.trim().length > 0
          if (!hasName) return null
          if (image === undefined) return getDefaultTeamImage(index)
          return image
        })
      })
      setSelections({})
    })
  }

  const handlePickWinner = React.useCallback(
    (
      roundIndex: number,
      matchIndex: number,
      side: WinnerSide,
      name: string,
    ) => {
      if (!name) return

      setSelections((prev) => {
        const roundSelections = {
          ...(prev[roundIndex] ?? {}),
          [matchIndex]: side,
        }
        return clearDownstreamSelections(
          { ...prev, [roundIndex]: roundSelections },
          roundIndex,
          matchIndex,
          rounds.length,
        )
      })
    },
    [rounds.length],
  )

  const handleRenameSeed = React.useCallback(
    (index: number, value: string) => {
      const previousName = participants[index]
      if (previousName === value) return

      setParticipants((prev) => {
        const next = [...prev]
        next[index] = value
        return next
      })
      if (!value.trim()) {
        setParticipantImages((prev) => {
          const next = [...prev]
          next[index] = null
          return next
        })
      }
      // 名单索引与抽签位置不同，按实际对阵位置清理受影响的赛果。
      const drawIndex = seedOrder ? seedOrder.indexOf(index) : index
      setSelections((prev) =>
        updateSelectionsAfterSeedRename(
          prev,
          previousName,
          value,
          drawIndex,
          rounds.length,
        ),
      )
    },
    [participants, seedOrder, rounds.length],
  )

  const handleResetWinners = () => resetSelections()

  const handleClearAll = () => {
    const size = normalizedTeamCount
    setSeedOrder(seededPlacement ? createSeededOrder(size) : null)
    setParticipants(Array(size).fill(''))
    setParticipantImages(createPlaceholderImages(size))
    resetSelections()
  }

  const handleSample = () => {
    const size = normalizedTeamCount
    setSeedOrder(seededPlacement ? createSeededOrder(size) : null)
    setParticipants(createSample(size))
    setParticipantImages(createPlaceholderImages(size))
    resetSelections()
  }

  const applyImageToSeed = React.useCallback(
    (index: number, image: string | null) => {
      setParticipantImages((prev) => {
        const length = normalizedTeamCount
        const next = prev.slice(0, length)
        while (next.length < length) next.push(getDefaultTeamImage(next.length))
        if (index >= 0 && index < next.length) {
          next[index] = image
        }
        return next
      })
    },
    [normalizedTeamCount],
  )

  const handleInsertImage = React.useCallback(
    (image: string, targetIndex: number | null) => {
      setParticipantImages((prev) => {
        const length = normalizedTeamCount
        const next = prev.slice(0, length)
        while (next.length < length) next.push(getDefaultTeamImage(next.length))

        const findNextSlot = () => {
          for (let i = 0; i < next.length; i++) {
            const value = next[i]
            if (!value || isDefaultTeamImage(value)) {
              return i
            }
          }
          return -1
        }

        const resolvedIndex =
          targetIndex !== null && targetIndex >= 0 && targetIndex < next.length
            ? targetIndex
            : findNextSlot()

        const slot = resolvedIndex >= 0 ? resolvedIndex : 0
        next[slot] = image
        return next
      })
    },
    [normalizedTeamCount],
  )

  const handleInsertSequence = React.useCallback(
    (images: string[]) => {
      if (!images.length) return
      setParticipantImages((prev) => {
        const length = normalizedTeamCount
        const next = prev.slice(0, length)
        while (next.length < length) next.push(getDefaultTeamImage(next.length))

        images.slice(0, length).forEach((image, index) => {
          next[index] = image
        })
        return next
      })
    },
    [normalizedTeamCount],
  )

  const clearSeedImage = React.useCallback(
    (seedIndex: number) => applyImageToSeed(seedIndex, null),
    [applyImageToSeed],
  )
  const openImageDialog = React.useCallback((index: number | null = null) => {
    setTargetImageIndex(index)
    setIsImageDialogOpen(true)
  }, [])

  const closeImageDialog = () => {
    setIsImageDialogOpen(false)
    setTargetImageIndex(null)
  }

  const dialogPortalTarget =
    isFullscreen && containerRef.current ? containerRef.current : portalTarget

  const toggleFullscreen = async () => {
    if (!containerRef.current) return

    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen()
      } else {
        await document.exitFullscreen()
      }
    } catch (error) {
      console.error(error)
    }
  }

  const adjustZoom = (delta: number) => {
    setZoom((value) => clampZoomValue(value + delta))
  }

  const handleExport = async () => {
    if (!bracketRef.current || isExporting || isUpdating) return
    // 输出前同步恢复所有比赛卡片，避免可见区域裁剪进入 PNG。
    flushSync(() => {
      setIsExporting(true)
      setExportImageUrl(null)
      setExportError(null)
      setShowExportPreview(false)
    })
    try {
      // 先交还主线程，让进度提示在大图克隆前显示。
      await new Promise<void>((resolve) => window.setTimeout(resolve, 0))
      if (!exportMountedRef.current) return
      const isTouchDevice = navigator.maxTouchPoints > 0
      const exportOptions = getBracketExportOptions({
        bracketHeight,
        bracketWidth,
        zoom,
        maxSide: isTouchDevice ? 4096 : 8192,
        maxPixels: isTouchDevice ? 16_000_000 : 32_000_000,
      })
      const blob = await createBracketExportImage(
        bracketRef.current,
        exportOptions,
        (canvas) => drawExportWatermark(canvas, exportOptions.width),
      )
      if (!exportMountedRef.current) return
      const url = URL.createObjectURL(blob)
      setExportImageUrl(url)
      // 保留真实保存入口，供未接管自动下载的内置浏览器再次点击。
      try {
        triggerBracketDownload(url)
      } catch (error) {
        console.error('Automatic bracket download failed', error)
      }
      try {
        plausible('BracketExport', {
          props: {
            locale,
            team_count: normalizedTeamCount,
            display_mode: displayMode,
            structure,
            direction,
          },
        })
      } catch {}
    } catch (error) {
      console.error('Bracket export failed', error)
      if (!exportMountedRef.current) return
      setExportError(
        t(
          error instanceof Error && error.name === 'TimeoutError'
            ? 'maker.exportTimeout'
            : 'maker.exportFailed',
        ),
      )
    } finally {
      if (exportMountedRef.current) setIsExporting(false)
    }
  }

  const startTitleEditing = () => setIsEditingTitle(true)

  const finishTitleEditing = () => {
    setBracketTitle((value) => {
      const trimmed = value.trim()
      return trimmed.length ? trimmed : defaultBracketTitle
    })
    setIsEditingTitle(false)
  }

  const cancelTitleEditing = () => {
    setIsEditingTitle(false)
    setBracketTitle((value) => {
      const trimmed = value.trim()
      return trimmed.length ? trimmed : defaultBracketTitle
    })
  }

  // 映射回原始名单，同时保持回调引用稳定，避免大对阵图重新渲染所有卡片。
  const clearDrawImage = React.useCallback(
    (index: number) => clearSeedImage(seedOrder?.[index] ?? index),
    [clearSeedImage, seedOrder],
  )
  const renameDrawSeed = React.useCallback(
    (index: number, value: string) =>
      handleRenameSeed(seedOrder?.[index] ?? index, value),
    [handleRenameSeed, seedOrder],
  )
  const requestDrawImage = React.useCallback(
    (index: number) => openImageDialog(seedOrder?.[index] ?? index),
    [openImageDialog, seedOrder],
  )

  const toggleNamesLabel = showNames ? copy.hideNames : copy.showNames
  const canvasProps = React.useMemo(
    () => ({
      bracketHeight,
      bracketWidth,
      bracketTitle,
      champion,
      championPlacement,
      columnHeaders,
      columnOffsets,
      columnWidths,
      connectors,
      copy,
      direction,
      displayMode,
      matchLayout,
      normalizedTeamCount,
      seedOrder,
      appearance,
      onClearSeedImage: clearDrawImage,
      onPickWinner: handlePickWinner,
      onRenameSeed: renameDrawSeed,
      onRequestSeedImage: requestDrawImage,
      rounds,
      showNames,
      structure,
      theme,
      verticalContentWidth: renderedDimensions.verticalContentWidth,
      verticalReverse: shouldReverseVerticalAxis,
      verticalYAxisLength: dimensions.bracketWidth,
      verticalXOffset,
    }),
    [
      bracketHeight,
      bracketWidth,
      bracketTitle,
      champion,
      championPlacement,
      columnHeaders,
      columnOffsets,
      columnWidths,
      connectors,
      copy,
      direction,
      displayMode,
      matchLayout,
      normalizedTeamCount,
      seedOrder,
      appearance,
      clearDrawImage,
      handlePickWinner,
      renameDrawSeed,
      requestDrawImage,
      rounds,
      showNames,
      structure,
      theme,
      renderedDimensions.verticalContentWidth,
      shouldReverseVerticalAxis,
      dimensions.bracketWidth,
      verticalXOffset,
    ],
  )
  const renderCanvas = React.useCallback(
    (area: BracketVisibleArea | null) => (
      <BracketCanvas {...canvasProps} visibleArea={area} />
    ),
    [canvasProps],
  )

  return (
    <>
      <section id="bracket" className="w-full min-w-0 scroll-mt-24">
        <div className="relative flex flex-col gap-3" aria-busy={isUpdating}>
          <span
            className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center"
            role="status"
          >
            {isUpdating && (
              <span className="rounded border bg-background px-3 py-1 text-sm shadow-sm">
                {t('maker.updating')}
              </span>
            )}
          </span>
          <BracketControlsPanel
            seededPlacement={seededPlacement}
            appearance={appearance}
            onAppearanceChange={(patch) =>
              setAppearance((current) => ({ ...current, ...patch }))
            }
            appearanceStorageError={appearanceStorageError}
            onShuffle={() => {
              const hasResults = Object.values(selections).some(
                (round) => Object.keys(round).length > 0,
              )
              if (hasResults && !window.confirm(t('maker.shuffleConfirm')))
                return
              const shuffled = shuffleSeedOrder(normalizedTeamCount)
              setSeedOrder(
                seededPlacement
                  ? createSeededOrder(normalizedTeamCount, shuffled)
                  : shuffled,
              )
              setSelections({})
              setShuffleCount((count) => count + 1)
            }}
            shuffleCount={shuffleCount}
            backgroundSettings={backgroundSettings}
            copy={copy}
            direction={direction}
            displayMode={displayMode}
            displayModeOptions={displayModeOptions}
            directionOptions={directionOptions}
            onBackgroundPresetChange={handleBackgroundPresetChange}
            onBackgroundRemove={handleBackgroundRemove}
            onBackgroundSettingChange={updateBackgroundSettings}
            onBackgroundUpload={handleBackgroundUpload}
            onClearAll={handleClearAll}
            onDirectionChange={setDirection}
            onDisplayModeChange={setDisplayMode}
            onFillSample={handleSample}
            onNamesChange={handleNamesChange}
            onOpenImageDialog={() => openImageDialog(null)}
            onStructureChange={setStructure}
            onTeamCountChange={handleTeamCountChange}
            onToggleNames={() => setShowNames((prev) => !prev)}
            participants={participants}
            showNames={showNames}
            structure={structure}
            structureOptions={structureOptions}
            teamCount={teamCount}
            theme={theme}
            toggleNamesLabel={toggleNamesLabel}
          />

          <div
            ref={containerRef}
            style={{
              display: isFullscreen ? 'flex' : undefined,
              flexDirection: isFullscreen ? 'column' : undefined,
            }}
            className="group relative overflow-hidden border bg-background/50 shadow-none print:overflow-visible print:shadow-none"
          >
            <BracketToolbar
              bracketTitle={bracketTitle}
              copy={copy}
              defaultBracketTitle={defaultBracketTitle}
              isEditingTitle={isEditingTitle}
              isExporting={isExporting}
              isUpdating={isUpdating}
              isFullscreen={isFullscreen}
              onCancelTitleEditing={cancelTitleEditing}
              onExport={handleExport}
              onFinishTitleEditing={finishTitleEditing}
              onResetWinners={handleResetWinners}
              onStartTitleEditing={startTitleEditing}
              onTitleChange={setBracketTitle}
              onToggleFullscreen={toggleFullscreen}
              onZoomIn={() => adjustZoom(0.1)}
              onZoomOut={() => adjustZoom(-0.1)}
              theme={theme}
              titleInputRef={titleInputRef}
              zoom={zoom}
            />

            <div className="px-3 text-sm" aria-live="polite" aria-atomic="true">
              {isExporting && (
                <p className="py-2" role="status">
                  {t('maker.exporting')}
                </p>
              )}
              {exportError && (
                <p className="py-2 text-destructive" role="alert">
                  {exportError}
                </p>
              )}
              {exportImageUrl && (
                <div className="space-y-2 py-2">
                  <p>{t('maker.exportReady')}</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <a
                      href={exportImageUrl}
                      download="snapbracket.png"
                      className="font-medium text-primary underline underline-offset-4"
                    >
                      {t('maker.savePng')}
                    </a>
                    <button
                      type="button"
                      className="text-primary underline underline-offset-4"
                      aria-expanded={showExportPreview}
                      onClick={() => setShowExportPreview((value) => !value)}
                    >
                      {t('maker.exportPreview')}
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t('maker.exportSaveHint')}
                  </p>
                  {showExportPreview && (
                    <NextImage
                      unoptimized
                      src={exportImageUrl}
                      alt={t('maker.exportPreview')}
                      width={1400}
                      height={800}
                      className="mx-auto h-auto max-h-[60vh] w-auto max-w-full border object-contain"
                    />
                  )}
                </div>
              )}
            </div>

            <BracketViewport
              key={exampleLoadVersion}
              backgroundLayerStyle={backgroundLayerStyle}
              backgroundOverlayStyle={backgroundOverlayStyle}
              bracketHeight={bracketHeight}
              bracketWidth={bracketWidth}
              className={theme.canvasContainer}
              isFullscreen={isFullscreen}
              isPrinting={isPrinting}
              label={bracketTitle}
              onZoomChange={setZoom}
              position={backgroundSettings.position}
              printBackground={backgroundSettings.print}
              renderAllMatches={isExporting || isPrinting}
              stageRef={bracketRef}
              zoom={zoom}
            >
              {renderCanvas}
            </BracketViewport>
          </div>
        </div>
      </section>
      <Dialog.Root
        open={isExampleConfirmationOpen}
        onOpenChange={setIsExampleConfirmationOpen}
      >
        <Dialog.Portal container={dialogPortalTarget}>
          <Dialog.Overlay className="fixed inset-0 z-[110] bg-background/60 backdrop-blur-sm" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-[111] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 space-y-4 border border-border bg-card p-6 shadow-xl">
            <Dialog.Title className="text-lg font-semibold">
              {t('maker.loadExampleTitle')}
            </Dialog.Title>
            <Dialog.Description className="text-sm leading-relaxed text-muted-foreground">
              {t('maker.replaceWithExample')}
            </Dialog.Description>
            <div className="flex flex-wrap justify-end gap-3">
              <Dialog.Close asChild>
                <Button variant="outline">{t('maker.keepBracket')}</Button>
              </Dialog.Close>
              <Button
                disabled={isExporting || isUpdating}
                onClick={() => {
                  setIsExampleConfirmationOpen(false)
                  applyBracketExample()
                }}
              >
                {t('maker.loadExampleAction')}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <ImageSelectorDialog
        open={isImageDialogOpen}
        onClose={closeImageDialog}
        onInsert={handleInsertImage}
        onInsertSequence={handleInsertSequence}
        targetIndex={targetImageIndex}
        portalTarget={dialogPortalTarget}
      />
    </>
  )
}
